import React, { useState, useEffect } from 'react';
import CarRatesTab from '@/components/admin/tabs/CarRatesTab';
import { useCars } from '@/hooks/useCars';
import { toast } from 'sonner';
import axios from 'axios';

/**
 * Car Rates Management Component
 * Wrapper for CarRatesTab with all required state management
 */
export const CarRatesManagement = ({ API }) => {
  const { cars, carTypes } = useCars(API);
  
  const [carRates, setCarRates] = useState([]);
  const [selectedCar, setSelectedCar] = useState(null);
  const [showCarRateDialog, setShowCarRateDialog] = useState(false);
  const [editingCarRate, setEditingCarRate] = useState(null);
  const [carRateForm, setCarRateForm] = useState({
    car_type_id: '',
    start_date: '',
    end_date: '',
    price: ''
  });

  // Fetch car rates when a car type is selected
  useEffect(() => {
    if (selectedCar) {
      fetchCarRates();
    }
  }, [selectedCar]);

  const fetchCarRates = async () => {
    if (!selectedCar) return;
    
    try {
      const carTypeId = selectedCar.replace('type-', '');
      const response = await axios.get(`${API}/admin/car-rates?car_type_id=${carTypeId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      setCarRates(response.data);
    } catch (error) {
      console.error('Error fetching car rates:', error);
      toast.error('Failed to fetch car rates');
    }
  };

  const resetCarRateForm = () => {
    setCarRateForm({
      car_type_id: selectedCar ? selectedCar.replace('type-', '') : '',
      start_date: '',
      end_date: '',
      price: ''
    });
    setEditingCarRate(null);
  };

  const handleCreateCarRate = async (e) => {
    e.preventDefault();
    try {
      // Ensure car_type_id is set from selectedCar
      const payload = {
        ...carRateForm,
        car_type_id: selectedCar ? selectedCar.replace('type-', '') : carRateForm.car_type_id,
        price: parseFloat(carRateForm.price)
      };
      
      await axios.post(`${API}/admin/car-rates`, payload, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      toast.success('Car rate created successfully');
      setShowCarRateDialog(false);
      resetCarRateForm();
      await fetchCarRates();
    } catch (error) {
      console.error('Error creating car rate:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to create car rate';
      toast.error(errorMsg);
    }
  };

  const handleUpdateCarRate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...carRateForm,
        car_type_id: selectedCar ? selectedCar.replace('type-', '') : carRateForm.car_type_id,
        price: parseFloat(carRateForm.price)
      };
      
      await axios.put(`${API}/admin/car-rates/${editingCarRate}`, payload, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      toast.success('Car rate updated successfully');
      setShowCarRateDialog(false);
      resetCarRateForm();
      await fetchCarRates();
    } catch (error) {
      console.error('Error updating car rate:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to update car rate';
      toast.error(errorMsg);
    }
  };

  const openEditCarRate = (carRate) => {
    setEditingCarRate(carRate.id);
    setCarRateForm({
      car_type_id: carRate.car_type_id,
      start_date: carRate.start_date,
      end_date: carRate.end_date,
      price: carRate.price || carRate.daily_rate || ''
    });
    setShowCarRateDialog(true);
  };

  const handleDeleteCarRate = async (carRateId) => {
    if (!window.confirm('Are you sure you want to delete this car rate?')) return;
    
    try {
      await axios.delete(`${API}/admin/car-rates/${carRateId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      toast.success('Car rate deleted successfully');
      await fetchCarRates();
    } catch (error) {
      console.error('Error deleting car rate:', error);
      toast.error('Failed to delete car rate');
    }
  };

  const openCarRateManagement = (carId) => {
    setSelectedCar(carId);
  };

  return (
    <CarRatesTab
      carTypes={carTypes}
      cars={cars}
      selectedCar={selectedCar}
      openCarRateManagement={openCarRateManagement}
      carRates={carRates}
      showCarRateDialog={showCarRateDialog}
      setShowCarRateDialog={setShowCarRateDialog}
      editingCarRate={editingCarRate}
      setEditingCarRate={setEditingCarRate}
      carRateForm={carRateForm}
      setCarRateForm={setCarRateForm}
      resetCarRateForm={resetCarRateForm}
      handleCreateCarRate={handleCreateCarRate}
      handleUpdateCarRate={handleUpdateCarRate}
      openEditCarRate={openEditCarRate}
      handleDeleteCarRate={handleDeleteCarRate}
    />
  );
};
